import React from 'react';
import {Select, TextInput} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Flex} from 'src/design/atoms/Flex';
import type {
  NotificationRuleFormChangeCondition,
  NotificationRuleMetadata} from 'src/api/ProactApiClient';
import {
  NotificationRuleChangeConditionPropertyEnum
} from 'src/api/ProactApiClient';
import {RemovableCard} from 'src/design/organisms/RemovableCard';
import type {
  ChangeEventConditionData,
  ChangeEventModuleConfig,
} from '../config/changeEventConfig.types';
import {EqualityOperatorOptions} from '../notificationRuleHelpers';
import {FormFieldSelector} from './FormFieldSelector';

export interface ChangeEventConditionRowProps {
  config: ChangeEventModuleConfig;
  data: ChangeEventConditionData;
  index: number;
  disabled: boolean;
  metadata: NotificationRuleMetadata;
  onChange: (data: ChangeEventConditionData, index: number) => void;
  onDelete: (data: ChangeEventConditionData, index: number) => void;
}

/**
 * Generic condition row component that renders based on module configuration.
 * Replaces all module-specific EditNotification*ChangeEventRule components.
 */
export const ChangeEventConditionRow: React.FC<ChangeEventConditionRowProps> =
  ({config, data, index, disabled, metadata, onChange, onDelete}) => {
    // Find the value config for the current property
    const currentValueConfig = config.valueConfigs.find(
      vc => vc.property === data.property
    );

    const handlePropertyChange = (value: string | null) => {
      if (!value) return;
      const newProperty = Number(
        value
      ) as NotificationRuleChangeConditionPropertyEnum;
      const newValueConfig = config.valueConfigs.find(
        vc => vc.property === newProperty
      );

      onChange(
        {
          ...data,
          property: newProperty,
          value: newValueConfig?.defaultValue ?? '',
          // Clear formFieldInvariantId when property changes (for Form module)
          ...(config.usesFormChangeCondition && {formFieldInvariantId: ''}),
        },
        index
      );
    };

    const handleOperatorChange = (value: string | null) => {
      if (!value) return;
      onChange({...data, operator: Number(value)}, index);
    };

    const handleValueChange = (value: string | null) => {
      onChange({...data, value: value ?? ''}, index);
    };

    const handleTextValueChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      onChange({...data, value: event.target.value}, index);
    };

    const handleFormFieldChange = (formFieldInvariantId: string) => {
      onChange(
        {...data, formFieldInvariantId} as NotificationRuleFormChangeCondition,
        index
      );
    };

    const handleDelete = () => {
      onDelete(data, index);
    };

    // Check if we're in Form module with InputValueChange property
    const showFormFieldSelector =
      config.usesFormChangeCondition &&
      data.property ===
        NotificationRuleChangeConditionPropertyEnum.InputValueChange;

    // Get description for the selected property
    const propertyDescription = currentValueConfig?.description;

    return (
      <RemovableCard
        index={index}
        onDelete={handleDelete}
        disabled={disabled}
        description={propertyDescription}>
        {currentValueConfig?.advisory && (
          <Alert color="blue" variant="light" mb="md">
            {currentValueConfig.advisory}
          </Alert>
        )}
        <Flex
          direction={{base: 'column', lg: 'row'}}
          gap="md"
          align={{base: 'stretch', lg: 'flex-end'}}
          w="100%">
          <Select
            label="Property"
            data-testid="property-select"
            data={config.propertyOptions}
            value={String(data.property)}
            onChange={handlePropertyChange}
            disabled={disabled}
            style={{flex: 2}}
          />

          {/* Form module special case: field selector */}
          {showFormFieldSelector && (
            <FormFieldSelector
              formDefinitionId={metadata.formDefinitionId!}
              value={
                (data as NotificationRuleFormChangeCondition)
                  .formFieldInvariantId
              }
              onChange={handleFormFieldChange}
              disabled={disabled}
            />
          )}

          <Select
            label="Operator"
            data-testid="operator-select"
            data={EqualityOperatorOptions}
            value={String(data.operator)}
            onChange={handleOperatorChange}
            disabled={disabled}
            style={{flex: 1}}
          />

          {/* Value selector - either dropdown or text input based on config */}
          {currentValueConfig &&
            (currentValueConfig.sourceType === 'text' ? (
              <TextInput
                label="Value"
                data-testid="value-text-input"
                value={data.value ?? ''}
                onChange={handleTextValueChange}
                disabled={disabled}
                style={{flex: 2}}
              />
            ) : (
              <Select
                label="Value"
                data-testid="value-select"
                data={currentValueConfig.options ?? []}
                value={String(data.value)}
                onChange={handleValueChange}
                disabled={disabled}
                style={{flex: 2}}
              />
            ))}
        </Flex>
      </RemovableCard>
    );
  };
